import {
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import { UserAvatar, RegistrationPlate } from '@/components/common';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { channelColors } from '@/styles/theme';
import type { Approval, ApprovalStatus, ApprovalType } from '@/types';
import { dateFnsLocale } from '@/utils/locale';

interface ApprovalDetailModalProps {
  approval: Approval | null;
  open: boolean;
  onClose: () => void;
  onApprove?: (approvalId: string) => void;
  onReject?: (approvalId: string) => void;
  onResend?: (approvalId: string) => void;
}

const statusColors: Record<ApprovalStatus, 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  expired: 'default',
};

export function ApprovalDetailModal({
  approval,
  open,
  onClose,
  onApprove,
  onReject,
  onResend,
}: ApprovalDetailModalProps) {
  const { t } = useTranslation('approvals');
  const theme = useTheme();

  // Resolve theme colors for chips (need alpha for backgrounds)
  const typeColors: Record<ApprovalType, string> = {
    extra_work: theme.palette.primary.light,
    high_value: theme.palette.warning.dark,
    customer_declined: theme.palette.error.light,
    warranty_claim: theme.palette.secondary.light,
    discount_request: theme.palette.info.main,
    additional_work: theme.palette.primary.light,
    parts_replacement: theme.palette.secondary.light,
    inspection_finding: theme.palette.warning.main,
    estimate_change: theme.palette.warning.dark,
  };

  const urgencyColors = {
    immediate: theme.palette.error.main,
    soon: theme.palette.warning.main,
    future: theme.palette.success.main,
  };

  const statusLabels: Record<ApprovalStatus, string> = {
    pending: t('pending'),
    approved: t('approved'),
    rejected: t('rejected'),
    expired: t('expired'),
  };

  const typeLabels: Record<ApprovalType, string> = {
    extra_work: t('extraWork'),
    high_value: t('highValue'),
    customer_declined: t('customerDeclined'),
    warranty_claim: t('warrantyClaim'),
    discount_request: t('discountRequest'),
    additional_work: t('additionalWork'),
    parts_replacement: t('partsReplacement'),
    inspection_finding: t('inspectionFinding'),
    estimate_change: t('estimateChange'),
  };

  const urgencyLabels: Record<string, string> = {
    immediate: t('immediate'),
    soon: t('soon'),
    future: t('future'),
  };

  if (!approval) return null;

  const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2)}`;

  const isExpired =
    approval.status === 'pending' &&
    approval.expiresAt &&
    isPast(new Date(approval.expiresAt));

  const isExpiringSoon =
    approval.status === 'pending' &&
    approval.expiresAt &&
    !isPast(new Date(approval.expiresAt)) &&
    new Date(approval.expiresAt).getTime() - Date.now() < 2 * 60 * 60 * 1000;

  return (
    <ResponsiveDialog mobileTitle={t('details')} open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogHeader
        title={approval.title}
        extra={approval.urgency === 'immediate' ? <WarningIcon sx={{ color: 'error.main' }} /> : undefined}
        onClose={onClose}
      />

      <DialogContent dividers>
        {/* Status and Type */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Chip
            size="small"
            label={typeLabels[approval.type] ?? approval.type}
            sx={{
              backgroundColor: alpha(typeColors[approval.type] ?? theme.palette.primary.light, 0.08),
              color: typeColors[approval.type] ?? theme.palette.primary.light,
              fontWeight: 500,
            }}
          />
          <Chip
            size="small"
            label={isExpired ? t('expired') : statusLabels[approval.status]}
            color={isExpired ? 'default' : statusColors[approval.status]}
          />
          <Chip
            size="small"
            label={urgencyLabels[approval.urgency]}
            sx={{
              backgroundColor: alpha(urgencyColors[approval.urgency], 0.08),
              color: urgencyColors[approval.urgency],
            }}
          />
        </Box>

        {/* Info Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
          {/* Vehicle */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DirectionsCarIcon color="action" fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">
                {t('vehicle')}
              </Typography>
            </Box>
            <RegistrationPlate registrationNumber={approval.vehicle.registrationNumber ?? '-'} />
            <Typography variant="body2">
              {approval.vehicle.make} {approval.vehicle.model} ({approval.vehicle.year})
            </Typography>
          </Paper>

          {/* Customer */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonIcon color="action" fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">
                {t('customer')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <UserAvatar
                firstName={approval.customer.firstName}
                lastName={approval.customer.lastName}
                size={32}
                variant="rounded"
              />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {approval.customer.firstName} {approval.customer.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {approval.customer.phone}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Work Order */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('workOrder')}
          </Typography>
          <Typography variant="body2">{approval.workOrderNumber}</Typography>
        </Box>

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('description')}
          </Typography>
          <Typography variant="body2">{approval.description}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Items */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('items')}
        </Typography>
        <List dense disablePadding sx={{ mb: 2 }}>
          {approval.items.map((item) => (
            <ListItem
              key={item.id}
              disablePadding
              sx={{ py: 0.5 }}
              secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {item.approved !== undefined && (
                    item.approved ? (
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    )
                  )}
                  <Typography variant="body2" fontWeight={500}>
                    {formatPrice(item.priceCents)}
                  </Typography>
                </Box>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">{item.name}</Typography>
                    <Chip
                      label={item.isRequired ? t('required') : t('optional')}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        backgroundColor: item.isRequired ? 'error.light' : 'grey.200',
                        color: item.isRequired ? 'error.dark' : 'text.secondary',
                      }}
                    />
                  </Box>
                }
                secondary={item.description}
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {t('total')}: {formatPrice(approval.totalCents)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Timestamps */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('requestedAt')}
            </Typography>
            <Typography variant="body2">
              {format(new Date(approval.requestedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
            </Typography>
          </Box>

          {approval.expiresAt && approval.status === 'pending' && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('expiresAt')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon
                  sx={{
                    fontSize: 14,
                    color: isExpiringSoon ? 'error.main' : isExpired ? 'text.disabled' : 'text.secondary',
                  }}
                />
                <Typography
                  variant="body2"
                  color={isExpiringSoon ? 'error.main' : isExpired ? 'text.disabled' : 'text.primary'}
                >
                  {isExpired
                    ? t('alreadyExpired')
                    : formatDistanceToNow(new Date(approval.expiresAt), {
                        addSuffix: true,
                        locale: dateFnsLocale(),
                      })}
                </Typography>
              </Box>
            </Box>
          )}

          {approval.respondedAt && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('respondedAt')}
              </Typography>
              <Typography variant="body2">
                {format(new Date(approval.respondedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('sentVia')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {approval.sentVia === 'whatsapp' && <WhatsAppIcon sx={{ fontSize: 16, color: channelColors.whatsapp }} />}
              <Typography variant="body2">{t(approval.sentVia)}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Customer Response */}
        {approval.customerResponse && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('customerResponse')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'action.hover' }}>
              <Typography variant="body2" fontStyle="italic">
                "{approval.customerResponse}"
              </Typography>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {approval.status === 'pending' && onResend && (
          <Button
            variant="outlined"
            startIcon={<WhatsAppIcon />}
            onClick={() => onResend(approval.id)}
          >
            {t('sendReminder')}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {approval.status === 'pending' && onReject && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => onReject(approval.id)}
          >
            {t('markRejected')}
          </Button>
        )}
        {approval.status === 'pending' && onApprove && (
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => onApprove(approval.id)}
          >
            {t('markApproved')}
          </Button>
        )}
        {approval.status !== 'pending' && (
          <Button variant="contained" onClick={onClose}>
            {t('close')}
          </Button>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
